import type { DemoScenarioResolved } from 'tio-data/demo/types';
import {
  isFeatureEnabled,
  type DemoSessionSelection,
} from 'tio-employee/utils/career-pathways-demo-data';
import { assertNever, type DemoProductConfig, type DemoScenario } from 'tio-employee/demo/types';
import { buildPslfDemo } from 'tio-employee/demo/products/pslf';
import { buildTasDemo } from 'tio-employee/demo/products/tas';
import { buildContributionsDemo } from 'tio-employee/demo/products/contributions';
import { buildCareerPathwaysDemo } from 'tio-employee/demo/products/career-pathways';
import { buildWellnessDemo } from 'tio-employee/demo/products/wellness';

/**
 * Dispatch a product config to its builder. The exhaustive switch + assertNever
 * guarantees every product kind has a builder at compile time — adding a kind
 * to the union without a case is a type error.
 */
function runBuilder(config: DemoProductConfig, scenario: DemoScenario): DemoScenarioResolved {
  switch (config.kind) {
    case 'pslf':
      return buildPslfDemo(config, scenario);
    case 'tas':
      return buildTasDemo(config, scenario);
    case 'contributions':
      return buildContributionsDemo(config, scenario);
    case 'career-pathways':
      return buildCareerPathwaysDemo(config, scenario);
    case 'wellness':
      return buildWellnessDemo(config, scenario);
    default:
      return assertNever(config);
  }
}

/**
 * Map the active demo selection onto the concrete product configs whose
 * fixtures should be served. Product availability is derived from the current
 * demo scenario's feature flags (e.g. a scenario with `pslf` enabled serves the
 * PSLF dashboard fixtures). Returns null when no demo products are active so
 * the handler stays a transparent pass-through.
 */
function scenarioFromSelection(selection: DemoSessionSelection): DemoScenario | null {
  const products: DemoProductConfig[] = [];

  if (isFeatureEnabled('pslf')) {
    products.push({ kind: 'pslf', scenarioId: 'full-journey' });
  }

  if (isFeatureEnabled('tuitionAssistance')) {
    products.push({ kind: 'tas', scenarioId: 'in-progress' });
  }

  if (products.length === 0) {
    return null;
  }

  return {
    persona: selection.userScenarioId ?? 'default',
    client: selection.clientId,
    role: selection.roleId,
    products,
  };
}

/**
 * Compile the active demo selection into the matchers + seed the tio-data demo
 * handler consumes. Returns null when no demo products are active.
 */
export function resolveDemoScenario(selection: DemoSessionSelection): DemoScenarioResolved | null {
  const scenario = scenarioFromSelection(selection);
  if (!scenario) {
    return null;
  }

  const matchers: DemoScenarioResolved['matchers'] = [];
  const seed: NonNullable<DemoScenarioResolved['seed']> = [];
  const fetchMatchers: NonNullable<DemoScenarioResolved['fetchMatchers']> = [];
  for (const product of scenario.products) {
    const part = runBuilder(product, scenario);
    matchers.push(...part.matchers);
    if (part.seed) {
      seed.push(...part.seed);
    }
    if (part.fetchMatchers) {
      fetchMatchers.push(...part.fetchMatchers);
    }
  }
  return { matchers, seed, fetchMatchers };
}
